import { exportElementToPdf } from '../../../utils/exportElementToPdf'
import {
  CLINICAL_ALERT_LIMIT,
  STATUS_LABELS,
  ageFromBirth,
  formatDateTime,
  formatDay,
  genderLabel
} from './patientFormat'

const DEFAULT_CLINIC = 'CLÍNICA MÉDICA INTEGRADA'
const GENERIC_CLINIC = new Set(['empresa atual', 'organização', 'organizacao', 'organization'])

function resolveClinicName(name) {
  const trimmed = String(name || '').trim()
  if (!trimmed || GENERIC_CLINIC.has(trimmed.toLowerCase())) return DEFAULT_CLINIC
  return trimmed
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function cssString(value) {
  return String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/</g, '')
    .replace(/\n/g, ' ')
}

function clinicInitials(name) {
  const skip = new Set(['de', 'da', 'do', 'dos', 'das', 'e'])
  const words = String(name || '')
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-zÀ-ÿ0-9]/g, ''))
    .filter((word) => word && !skip.has(word.toLowerCase()))
  const letters = words.slice(0, 2).map((word) => word[0].toUpperCase())
  return letters.join('') || 'CM'
}

function formatCpf(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length !== 11) return value || '—'
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

function formatMeasure(value) {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  if (!Number.isFinite(number)) return String(value)
  return number.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

function formatFixed(value, digits) {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  return number.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function insuranceLine(chart) {
  const provider = chart.insurance_provider || 'Particular'
  const plan = chart.insurance_plan ? ` (${chart.insurance_plan})` : ''
  const card = chart.insurance_card ? ` / ${chart.insurance_card}` : ''
  return `${provider}${plan}${card}`
}

function portrait(chart) {
  if (chart.photo_url) return `<img class="portrait" src="${esc(chart.photo_url)}" alt="" />`
  return `<div class="portrait">${esc(clinicInitials(chart.name || 'Paciente'))}</div>`
}

function field(label, value, wide) {
  return `<div class="field${wide ? ' field--wide' : ''}"><span class="field-label">${esc(label)}</span><span class="field-value">${esc(value || '—')}</span></div>`
}

function pills(items, modifier) {
  if (!items.length) return '<span class="muted">Nenhuma registrada</span>'
  return `<div class="pills">${items.map((item) => `<span class="pill ${modifier}">${esc(item)}</span>`).join('')}</div>`
}

function vitalCell(label, value, unit) {
  const filled = value !== '' && value !== null && value !== undefined
  return `<div class="vital">
    <span class="vital-label">${esc(label)}</span>
    <strong class="${filled ? '' : 'is-blank'}">${filled ? esc(value) : '&nbsp;'}</strong>
    <small>${esc(unit)}</small>
  </div>`
}

const MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
]

function formatLongDay(value) {
  const day = String(value || '').slice(0, 10)
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return formatDay(value)
  return `${match[3]} de ${MONTHS[Number(match[2]) - 1]} de ${match[1]}`
}

function issuedStamp() {
  return new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function buildCss(footerLeft) {
  return `
    @page {
      size: A4;
      margin: 15mm;
      @bottom-left {
        content: "${footerLeft}";
        font-family: "Segoe UI", Arial, sans-serif;
        font-size: 8pt;
        color: #718096;
      }
      @bottom-right {
        content: "Página " counter(page);
        font-family: "Segoe UI", Arial, sans-serif;
        font-size: 8pt;
        color: #718096;
      }
    }

    * { box-sizing: border-box; }

    html { background: #d9e2ec; }

    body {
      width: 210mm;
      min-height: 297mm;
      margin: 8mm auto;
      padding: 15mm;
      background: #fff;
      color: #2d3748;
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 12px;
      line-height: 1.35;
      box-shadow: 0 12px 40px rgba(26, 54, 93, 0.16);
    }

    .toolbar {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 2;
      display: flex;
      gap: 8px;
    }

    .toolbar button {
      border: 0;
      background: #1a365d;
      color: #fff;
      font: inherit;
      font-size: 13px;
      font-weight: 700;
      padding: 8px 14px;
      border-radius: 8px;
      cursor: pointer;
    }

    .toolbar button.secondary {
      background: #fff;
      color: #1a365d;
      box-shadow: inset 0 0 0 1px #1a365d;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      border-bottom: 2.5px solid #1a365d;
      padding-bottom: 8px;
    }

    .brand { display: flex; align-items: center; gap: 10px; min-width: 0; }

    .logo {
      width: 42px;
      height: 42px;
      border-radius: 8px;
      background: #1a365d;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      letter-spacing: 0.04em;
      flex-shrink: 0;
    }

    .brand h1 {
      margin: 0;
      color: #1a365d;
      font-size: 16px;
      letter-spacing: 0.04em;
      line-height: 1.15;
    }

    .subtitle { margin: 2px 0 0; color: #718096; font-size: 11px; }

    .contact {
      text-align: right;
      font-size: 11px;
      color: #4a5568;
      line-height: 1.45;
    }

    .contact p { margin: 0; }
    .contact strong { color: #1a365d; }

    .doc-title {
      margin: 8px 0 10px;
      text-align: center;
      color: #1a365d;
      font-size: 13px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    .section { margin-bottom: 3.2mm; }
    .section-title {
      margin: 0 0 4px;
      color: #1a365d;
      font-size: 11px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    .panel {
      background: #edf2f7;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 8px 10px;
    }

    .identity-row { display: flex; gap: 12px; align-items: flex-start; }
    .portrait {
      width: 18mm;
      height: 18mm;
      object-fit: cover;
      border-radius: 1rem;
      background: #f1f5f9;
      border: 2px solid #e2e8f0;
      color: #64748b;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      flex-shrink: 0;
    }
    .identity {
      flex: 1;
      display: grid;
      grid-template-columns: 1.5fr 1fr 1fr;
      gap: 6px 12px;
    }

    .field--wide { grid-column: span 2; }

    .field-label {
      display: block;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #4a5568;
    }

    .field-value { display: block; font-size: 12.5px; color: #1a202c; font-weight: 600; }

    .meta {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 14px;
      margin-top: 8px;
      padding-top: 6px;
      border-top: 1px solid #e2e8f0;
      font-size: 11px;
      color: #4a5568;
    }

    .meta strong { color: #1a365d; }

    .alerts {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .alert-box {
      background: #edf2f7;
      border: 1px solid #e2e8f0;
      border-left: 3px solid #1a365d;
      border-radius: 6px;
      padding: 7px 10px;
      min-height: 48px;
    }

    .alert-box--allergy { border-left-color: #c53030; }

    .alert-box h3 {
      margin: 0 0 4px;
      font-size: 10px;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: #1a365d;
    }

    .alert-box--allergy h3 { color: #9b2c2c; }

    .pills { display: flex; flex-wrap: wrap; gap: 4px; }

    .pill {
      background: #fff;
      border: 1px solid #cbd5e0;
      border-radius: 999px;
      padding: 1px 8px;
      font-size: 11px;
      font-weight: 700;
      color: #1a365d;
    }

    .pill--allergy {
      color: #9b2c2c;
      border-color: #fed7d7;
      background: #fff5f5;
    }

    .muted { color: #718096; font-size: 11px; }

    .vitals {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 6px;
      background: #edf2f7;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 7px 8px;
    }

    .vital { text-align: center; min-width: 0; }

    .vital-label {
      display: block;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #1a365d;
    }

    .vital strong {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      color: #1a202c;
      line-height: 1.2;
    }

    .vital strong.is-blank {
      border-bottom: 1px solid #a0aec0;
      min-height: 1.15em;
      margin: 4px 6px 0;
    }

    .vital small { color: #718096; font-size: 9px; }

    .ruled {
      min-height: 29.6mm;
      line-height: 7.4mm;
      font-size: 12px;
      color: #1a202c;
      text-align: justify;
      white-space: pre-wrap;
      background-color: #fff;
      background-image: repeating-linear-gradient(
        to bottom,
        transparent 0,
        transparent calc(7.4mm - 0.25mm),
        #e2e8f0 calc(7.4mm - 0.25mm),
        #e2e8f0 7.4mm
      );
      background-attachment: local;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 0 8px;
    }

    .ruled--long { min-height: 51.8mm; }
    .ruled strong { color: #1a365d; }

    .evolution-block { margin-bottom: 3.2mm; break-inside: avoid; }
    .evolution-block p { margin: 0 0 1.2mm; white-space: pre-wrap; text-align: justify; }
    .evolution-meta {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 2mm;
      font-size: 11px;
      color: #4a5568;
    }
    .evolution-meta strong { color: #1a365d; }
    .evolution-sign { margin-top: 2.4mm; text-align: right; }
    .evolution-sign p { margin: 0; text-align: right; }
    .evolution-sign .who { color: #1a365d; font-weight: 700; font-size: 11px; }
    .evolution-sign .signed { margin-top: 0.6mm; color: #1a365d; font-size: 10px; font-weight: 700; }

    .signature {
      margin-top: 10mm;
      text-align: center;
      break-inside: avoid;
    }

    .signature-line {
      width: 72mm;
      border-top: 1px solid #1a365d;
      margin: 0 auto 4px;
    }

    .signature-image {
      display: block;
      width: 62mm;
      height: 22mm;
      object-fit: contain;
      margin: 0 auto 2px;
    }

    .signature p { margin: 0; color: #4a5568; font-size: 11px; }
    .signature-council { color: #1a365d; font-weight: 800; letter-spacing: 0.04em; }
    .signature small { display: block; margin-top: 2px; color: #718096; font-size: 10px; }

    .header, .panel, .vitals, .alert-box, .signature { break-inside: avoid; }

    @media print {
      html, body {
        width: auto;
        min-height: 0;
        margin: 0;
        padding: 0;
        background: #fff;
        box-shadow: none;
      }

      .no-print { display: none !important; }

      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        box-shadow: none !important;
      }
    }
  `
}

function signatureBlock(issuedBy, issuedLong, signatureUrl, councilLine) {
  const image = signatureUrl
    ? `<img class="signature-image" src="${esc(signatureUrl)}" alt="Assinatura" />`
    : '<div class="signature-line"></div>'
  return `<div class="signature">
    ${image}
    <p>Assinatura e Carimbo do Profissional</p>
    ${councilLine ? `<p class="signature-council">${esc(councilLine)}</p>` : ''}
    <small>Emitido por: ${esc(issuedBy || '—')} · ${esc(issuedLong)}</small>
  </div>`
}

export function renderPatientRecordDocument(chart, options = {}) {
  const clinicName = resolveClinicName(options.clinicName)
  const issuedShort = new Date().toLocaleDateString('pt-BR')
  const issuedLong = issuedStamp()
  const age = ageFromBirth(chart.birth_date)
  const latest = (chart.vitals || [])[0]
  const allergies = (chart.allergies || []).map((item) => item.description).filter(Boolean)
  const conditions = (chart.alerts || []).map((item) => item.description).filter(Boolean).slice(0, 1).map((item) => (
    item.length > CLINICAL_ALERT_LIMIT ? `${item.slice(0, CLINICAL_ALERT_LIMIT - 3).trimEnd()}...` : item
  ))
  const evolutions = chart.evolutions || []
  const pressure = latest && (latest.systolic || latest.diastolic)
    ? `${latest.systolic || '—'} × ${latest.diastolic || '—'}`
    : ''
  const peso = chart.measures?.peso
  const altura = chart.measures?.altura
  const heightMeters = Number(altura?.value)
  const weightKg = Number(peso?.value)
  const imc = weightKg && heightMeters
    ? formatFixed(weightKg / (heightMeters * heightMeters), 1)
    : ''
  const contacts = [options.phone && `Tel. ${options.phone}`, options.email, options.address].filter(Boolean)
  const archived = chart.archived_at
    ? `Arquivado · ${chart.archive_reason === 'obito' ? 'Óbito' : 'Saída'} em ${formatDay(chart.archived_at)}`
    : ''
  const footerLeft = cssString(`${clinicName} · Emitido em ${issuedShort}`)
  const issuedBy = options.issuedBy || ''

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Prontuário ${esc(chart.record_number || '')} — ${esc(chart.name || 'Paciente')}</title>
  <style>${buildCss(footerLeft)}</style>
</head>
<body>
  <div class="toolbar no-print">
    <button type="button" onclick="window.print()">Imprimir</button>
    <button type="button" class="secondary" onclick="window.close()">Fechar</button>
  </div>

  <header class="header">
    <div class="brand">
      <div class="logo">${esc(clinicInitials(clinicName))}</div>
      <div>
        <h1>${esc(clinicName)}</h1>
        <p class="subtitle">Prontuário eletrônico de atendimento</p>
      </div>
    </div>
    <div class="contact">
      ${contacts.map((line) => `<p>${esc(line)}</p>`).join('')}
      <p><strong>Prontuário</strong> ${esc(chart.record_number || '—')}</p>
      <p><strong>Emissão</strong> ${esc(issuedLong)}</p>
      <p><strong>Emitido por:</strong> ${esc(issuedBy || '—')}</p>
    </div>
  </header>

  <h2 class="doc-title">Prontuário de Atendimento</h2>

  <section class="section">
    <h3 class="section-title">Identificação do paciente</h3>
    <div class="panel">
      <div class="identity-row">
        ${portrait(chart)}
        <div style="flex:1;min-width:0">
      <div class="identity">
        ${field('Nome', chart.name, true)}
        ${field('CPF', formatCpf(chart.cpf))}
        ${field('Data de nascto.', formatDay(chart.birth_date))}
        ${field('Idade', age != null ? `${age} anos` : '—')}
        ${field('Sexo', genderLabel(chart.gender))}
        ${field('Telefone', chart.phone || chart.whatsapp)}
        ${field('Convênio / Nº', insuranceLine(chart), true)}
      </div>
      <div class="meta">
        <span><strong>Admissão</strong> ${esc(formatDay(chart.admission_date))}</span>
        <span><strong>Situação</strong> ${esc(STATUS_LABELS[chart.status] || chart.status || '—')}</span>
        <span><strong>Leito</strong> ${esc(chart.bed || 'Sem leito fixo')}</span>
        <span><strong>Sangue</strong> ${esc(chart.blood_type || '—')}</span>
        ${archived ? `<span><strong>${esc(archived)}</strong></span>` : ''}
      </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <h3 class="section-title">Alertas médicos</h3>
    <div class="alerts">
      <div class="alert-box alert-box--allergy">
        <h3>Alergias</h3>
        ${pills(allergies, 'pill--allergy')}
      </div>
      <div class="alert-box">
        <h3>Alertas clínicos</h3>
        ${pills(conditions, '')}
      </div>
    </div>
  </section>

  <section class="section">
    <h3 class="section-title">Sinais vitais${latest ? ` · ${esc(formatDateTime(latest.recorded_at))}` : ''}</h3>
    <div class="vitals">
      ${vitalCell('PA', pressure, 'mmHg')}
      ${vitalCell('FC', latest ? formatMeasure(latest.heart_rate) : '', 'bpm')}
      ${vitalCell('Temp', latest ? formatMeasure(latest.temperature) : '', '°C')}
      ${vitalCell('Peso', peso ? formatFixed(peso.value, 1) : '', 'kg')}
      ${vitalCell('Altura', altura ? formatFixed(altura.value, 2) : '', 'm')}
      ${vitalCell('SpO2', latest ? formatMeasure(latest.oxygen_saturation) : '', '%')}
      ${vitalCell('IMC', imc, 'kg/m²')}
    </div>
  </section>

  <section class="section">
    <h3 class="section-title">Evoluções clínicas · ${evolutions.length} registro(s)</h3>
    ${evolutionArticles(evolutions)}
  </section>

  ${signatureBlock(issuedBy, issuedLong, options.signatureUrl, options.councilLine)}

  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print() }, 150)
    })
  </script>
</body>
</html>`
}

function openPrintDocument(html) {
  const win = window.open('', '_blank', 'width=1000,height=1100')
  if (!win) return false
  win.document.open()
  win.document.write(html)
  win.document.close()
  win.focus()
  return true
}

function sheetHeader({ clinicName, issuedShort, issuedFull, issuedBy, recordNumber, contacts }) {
  return `<header class="header">
    <div class="brand">
      <div class="logo">${esc(clinicInitials(clinicName))}</div>
      <div>
        <h1>${esc(clinicName)}</h1>
        <p class="subtitle">Prontuário eletrônico de atendimento</p>
      </div>
    </div>
    <div class="contact">
      ${contacts.map((line) => `<p>${esc(line)}</p>`).join('')}
      <p><strong>Prontuário</strong> ${esc(recordNumber || '—')}</p>
      <p><strong>Emissão</strong> ${esc(issuedFull || issuedShort)}</p>
      <p><strong>Emitido por:</strong> ${esc(issuedBy || '—')}</p>
    </div>
  </header>`
}

function evolutionArticles(list) {
  if (!list.length) return '<p class="muted">Nenhuma evolução no período selecionado.</p>'
  return list.map((item) => {
    const when = `${formatLongDay(item.evolution_date)} ${String(item.evolution_time || '').slice(0, 5)}`.trim()
    const who = [item.author_name, item.role, item.registration_number].filter(Boolean).join(' - ')
    const lines = [
      ['S - Subjetivo', item.subjective],
      ['O - Objetivo', item.objective],
      ['A - Avaliação', item.assessment],
      ['P - Plano e conduta', item.plan]
    ].filter(([, text]) => text)
    return `<article class="evolution-block panel">
      <div class="evolution-meta"><strong>${esc(when)}</strong></div>
      ${item.cid ? `<p class="muted">CID ${esc(item.cid)}</p>` : ''}
      ${lines.map(([label, text]) => `<p><strong>${esc(label)}</strong> ${esc(text)}</p>`).join('')}
      <div class="evolution-sign">
        ${who ? `<p class="who">${esc(who)}</p>` : ''}
        <p class="signed">assinado digitalmente</p>
      </div>
    </article>`
  }).join('')
}

export function renderEvolutionDocument(chart, options = {}) {
  const clinicName = resolveClinicName(options.clinicName)
  const issuedShort = new Date().toLocaleDateString('pt-BR')
  const issuedLong = issuedStamp()
  const issuedBy = options.issuedBy || ''
  const periodLabel = options.periodLabel || 'Todas as evoluções'
  const list = options.evolutions || chart.evolutions || []
  const age = ageFromBirth(chart.birth_date)
  const contacts = [options.phone && `Tel. ${options.phone}`, options.email, options.address].filter(Boolean)
  const footerLeft = cssString(`${clinicName} · Emitido em ${issuedShort}`)

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Evoluções ${esc(chart.record_number || '')} — ${esc(chart.name || 'Paciente')}</title>
  <style>${buildCss(footerLeft)}</style>
</head>
<body>
  <div class="toolbar no-print">
    <button type="button" onclick="window.print()">Imprimir</button>
    <button type="button" class="secondary" onclick="window.close()">Fechar</button>
  </div>
  ${sheetHeader({ clinicName, issuedShort, issuedFull: issuedLong, issuedBy, recordNumber: chart.record_number, contacts })}
  <h2 class="doc-title">Evoluções clínicas</h2>
  <section class="section">
    <h3 class="section-title">Identificação do paciente</h3>
    <div class="panel">
      <div class="identity-row">
        ${portrait(chart)}
        <div class="identity">
          ${field('Nome', chart.name, true)}
          ${field('Prontuário', chart.record_number)}
          ${field('Idade', age != null ? `${age} anos` : '—')}
          ${field('Período', periodLabel, true)}
        </div>
      </div>
    </div>
  </section>
  <section class="section">
    <h3 class="section-title">${esc(periodLabel)} · ${list.length} registro(s)</h3>
    ${evolutionArticles(list)}
  </section>
  ${signatureBlock(issuedBy, issuedLong, options.signatureUrl, options.councilLine)}
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print() }, 150)
    })
  </script>
</body>
</html>`
}

export function printPatientRecord(chart, options = {}) {
  return openPrintDocument(renderPatientRecordDocument(chart, options))
}

export function printPatientEvolutions(chart, options = {}) {
  return openPrintDocument(renderEvolutionDocument(chart, options))
}

const VITAL_SHEET_CSS = `
  .vital-print-sheet { width: 1040px; background: #fff; color: #0f172a; font-family: Arial, Helvetica, sans-serif; padding: 28px 32px 36px; }
  .vital-print-header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #0f766e; padding-bottom: 12px; }
  .vital-print-brand { display: flex; gap: 12px; align-items: center; }
  .vital-print-logo { width: 42px; height: 42px; border-radius: 10px; background: #0f766e; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; }
  .vital-print-header h1 { margin: 0; font-size: 18px; }
  .vital-print-header p, .vital-print-side p, .vital-print-meta { margin: 2px 0 0; color: #475569; font-size: 12px; }
  .vital-print-side { text-align: right; font-size: 12px; color: #334155; }
  .vital-print-title { margin: 18px 0 4px; font-size: 20px; }
  .vital-print-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 12px; }
  .vital-print-table th { text-align: left; background: #f0fdfa; color: #115e59; padding: 7px 8px; border-bottom: 1px solid #99f6e4; }
  .vital-print-table td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  .vital-print-footer { margin-top: 16px; font-size: 11px; color: #64748b; }
`

function vitalFileSlug(value) {
  return String(value || 'paciente')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'paciente'
}

function vitalReading(value, suffix = '') {
  if (value == null || value === '') return '—'
  return `${value}${suffix}`
}

function vitalSheetMarkup(chart, options = {}) {
  const clinicName = resolveClinicName(options.clinicName)
  const letters = clinicName.split(/\s+/).filter((word) => !/^(de|da|do|dos|das|e)$/i.test(word)).slice(0, 2).map((word) => word[0]?.toUpperCase() || '').join('') || 'CM'
  const list = options.vitals || []
  const periodLabel = options.periodLabel || 'Todas as aferições'
  const issuedAt = new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
  const rows = list.length
    ? list.map((item) => `<tr>
        <td>${esc(formatDateTime(item.recorded_at))}</td>
        <td>${esc(vitalReading(item.systolic))}/${esc(vitalReading(item.diastolic))}</td>
        <td>${esc(vitalReading(item.heart_rate, ' bpm'))}</td>
        <td>${esc(vitalReading(item.oxygen_saturation, '%'))}</td>
        <td>${esc(vitalReading(item.temperature, '°C'))}</td>
        <td>${esc(item.blood_glucose ? `${item.blood_glucose} mg/dL` : '—')}</td>
        <td>${esc(vitalReading(item.respiratory_rate, ' rpm'))}</td>
        <td>${esc(item.pain_scale == null || item.pain_scale === '' ? '—' : `${item.pain_scale}/10`)}</td>
        <td>${esc(item.recorded_by_name || '—')}${item.notes ? `<br>${esc(item.notes)}` : ''}</td>
      </tr>`).join('')
    : '<tr><td colspan="9">Nenhuma aferição neste período.</td></tr>'
  return `<article class="vital-print-sheet">
    <header class="vital-print-header">
      <div class="vital-print-brand">
        <div class="vital-print-logo">${esc(letters)}</div>
        <div>
          <h1>${esc(clinicName)}</h1>
          <p>Prontuário eletrônico de atendimento</p>
        </div>
      </div>
      <div class="vital-print-side">
        <p><strong>Paciente</strong> ${esc(chart.name || '—')}</p>
        <p><strong>Prontuário</strong> ${esc(chart.record_number || '—')}</p>
        <p><strong>Emissão</strong> ${esc(issuedAt)}</p>
        <p><strong>Emitido por</strong> ${esc(options.issuedBy || '—')}</p>
      </div>
    </header>
    <h2 class="vital-print-title">Sinais vitais</h2>
    <p class="vital-print-meta">${esc(periodLabel)} · ${list.length} registro(s)</p>
    <table class="vital-print-table">
      <thead>
        <tr><th>Data / hora</th><th>PA</th><th>FC</th><th>SpO2</th><th>Temp</th><th>Glicemia</th><th>FR</th><th>Dor</th><th>Responsável</th></tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <footer class="vital-print-footer">${esc(clinicName)} · ${esc(issuedAt)}</footer>
  </article>`
}

export function printPatientVitals(chart, options = {}) {
  const win = window.open('', '_blank', 'width=1100,height=800')
  if (!win) return false
  win.document.open()
  win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Sinais vitais ${esc(chart.record_number || '')} — ${esc(chart.name || 'Paciente')}</title>
  <style>
    ${VITAL_SHEET_CSS}
    body { margin: 0; background: #f8fafc; }
    .toolbar { display: flex; gap: 8px; padding: 12px 16px; background: #fff; border-bottom: 1px solid #e2e8f0; }
    .toolbar button { border: 0; border-radius: 8px; background: #0f766e; color: #fff; font-weight: 700; padding: 8px 14px; cursor: pointer; }
    .toolbar .secondary { background: #e2e8f0; color: #0f172a; }
    @page { size: landscape; margin: 10mm; }
    @media print { .toolbar { display: none; } body { background: #fff; } .vital-print-sheet { width: auto; padding: 0; } }
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" onclick="window.print()">Imprimir</button>
    <button type="button" class="secondary" onclick="window.close()">Fechar</button>
  </div>
  ${vitalSheetMarkup(chart, options)}
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print() }, 150) })</script>
</body>
</html>`)
  win.document.close()
  win.focus()
  return true
}

export async function downloadPatientVitals(chart, options = {}) {
  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;background:#fff;'
  host.innerHTML = `<style>${VITAL_SHEET_CSS}</style>${vitalSheetMarkup(chart, options)}`
  document.body.appendChild(host)
  try {
    const date = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')
    const sheet = host.querySelector('.vital-print-sheet')
    await exportElementToPdf(sheet, `Sinais-${vitalFileSlug(chart.name)}-${date}.pdf`, {
      orientation: 'l',
      rowSelector: '.vital-print-table tbody tr',
      scale: 2
    })
  } finally {
    host.remove()
  }
}
